import React, { useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const QUESTIONS = {
  round1: {
    title: 'Primary Analysis Focus',
    question: 'What is your primary focus for this analysis?',
    options: [
      {
        value: 'Investment/Financial Analysis',
        label: 'Investment/Financial Analysis',
        icon: '💰',
        description: 'Evaluate as potential investment opportunity'
      },
      {
        value: 'Employer/Business Partner Research',
        label: 'Employer/Business Partner Research',
        icon: '🤝',
        description: 'Assess as employer or business partner'
      },
      {
        value: 'Competitive Intelligence',
        label: 'Competitive Intelligence',
        icon: '🎯',
        description: 'Analyze competitive market position'
      }
    ]
  },
  round2: {
    title: 'Risk/Opportunity Focus',
    question: 'Which area would you like to focus on?',
    options: [
      {
        value: 'Regulatory & Legal Risks',
        label: 'Regulatory & Legal Risks',
        icon: '⚖️',
        description: 'Focus on legal and regulatory challenges'
      },
      {
        value: 'Supply Chain & Operational Risks',
        label: 'Supply Chain & Operational Risks',
        icon: '🔗',
        description: 'Examine operational dependencies and risks'
      },
      {
        value: 'Growth Opportunities & Expansion',
        label: 'Growth Opportunities & Expansion',
        icon: '📈',
        description: 'Identify growth potential and opportunities'
      }
    ]
  },
  round3: {
    title: 'Additional Context',
    question: 'What additional context would be most valuable?',
    options: [
      {
        value: 'Warning Signs & Red Flags',
        label: 'Warning Signs & Red Flags',
        icon: '⚠️',
        description: 'Identify potential concerns and risks'
      },
      {
        value: 'Sustainability & Long-Term Strategy',
        label: 'Sustainability & Long-Term Strategy',
        icon: '🌱',
        description: 'Analyze long-term viability and strategy'
      },
      {
        value: 'Customer & Revenue Concentration',
        label: 'Customer & Revenue Concentration',
        icon: '👥',
        description: 'Examine customer base and revenue sources'
      }
    ]
  }
};

export default function AnalysisQuestionsPage({
  selections,
  setSelections,
  onNext,
  onBack,
  userInfo,
  uploadedFiles,
  setReportInfo
}) {
  const [currentRound, setCurrentRound] = useState(1);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  const currentQuestion = QUESTIONS[`round${currentRound}`];
  const currentSelection = selections[`round${currentRound}`];

  const handleSelection = (value) => {
    setSelections(prev => ({ ...prev, [`round${currentRound}`]: value }));
    setError('');
  };

  const handleNext = () => {
    if (!currentSelection) {
      setError('Please make a selection to continue.');
      return;
    }

    if (currentRound < 3) {
      setCurrentRound(prev => prev + 1);
    } else {
      generateReport();
    }
  };

  const handleBack = () => {
    if (currentRound > 1) {
      setCurrentRound(prev => prev - 1);
    } else {
      onBack();
    }
  };

  const generateReport = async () => {
    setGenerating(true);
    setError('');

    try {
      // Get uploaded file IDs and names
      const uploadedFilesList = uploadedFiles.filter(f => f.status === 'uploaded');
      const fileIds = uploadedFilesList.map(f => f.id);
      const fileNames = uploadedFilesList.map(f => f.name);

      const requestData = {
        user_info: userInfo,
        file_ids: fileIds,
        file_names: fileNames,
        round1_selection: selections.round1,
        round2_selection: selections.round2,
        round3_selection: selections.round3
      };

      const response = await axios.post(`${API_URL}/api/generate-report`, requestData);

      setReportInfo(response.data);
      onNext();
    } catch (err) {
      console.error('Report generation error:', err);
      setError(
        err.response?.data?.detail ||
        'Failed to generate report. Please try again.'
      );
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-border p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-navy mb-2">
          Step 3 of 4: Customize Your Analysis
        </h2>
        <div className="w-32 h-1 bg-primary"></div>
        <p className="text-text-secondary mt-4">
          Question {currentRound} of 3
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-error rounded-lg">
          <p className="text-error text-sm">{error}</p>
        </div>
      )}

      {generating ? (
        <div className="text-center py-12">
          <div className="inline-block">
            <svg className="animate-spin h-16 w-16 text-primary mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-navy mb-2">
            Generating Your Report...
          </h3>
          <p className="text-text-secondary mb-2">
            Analyzing {uploadedFiles.filter(f => f.status === 'uploaded').length} document(s)
          </p>
          <p className="text-sm text-text-secondary">
            This may take 1-2 minutes. Please don't close this window.
          </p>
          <div className="mt-6 flex items-center justify-center space-x-2 text-sm text-text-secondary">
            <span className="animate-pulse">●</span>
            <span className="animate-pulse delay-100">●</span>
            <span className="animate-pulse delay-200">●</span>
          </div>
        </div>
      ) : (
        <>
          {/* Question */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-navy mb-2">
              {currentQuestion.title}
            </h3>
            <p className="text-text-secondary">
              {currentQuestion.question}
            </p>
          </div>

          {/* Options */}
          <div className="space-y-4 mb-8">
            {currentQuestion.options.map((option) => (
              <button
                key={option.value}
                onClick={() => handleSelection(option.value)}
                className={`w-full text-left p-6 border-2 rounded-lg transition-all hover:shadow-md ${
                  currentSelection === option.value
                    ? 'border-primary bg-blue-50'
                    : 'border-border hover:border-primary'
                }`}
              >
                <div className="flex items-start space-x-4">
                  <div className="text-4xl">{option.icon}</div>
                  <div className="flex-1">
                    <h4 className="text-lg font-semibold text-navy mb-1 flex items-center">
                      {option.label}
                      {currentSelection === option.value && (
                        <span className="ml-2 text-primary">✓</span>
                      )}
                    </h4>
                    <p className="text-text-secondary">{option.description}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-6 border-t border-border">
            <button
              onClick={handleBack}
              className="px-6 py-3 border border-border text-navy font-medium rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-2"
            >
              <span>←</span>
              <span>Back</span>
            </button>

            <button
              onClick={handleNext}
              disabled={!currentSelection}
              className="px-8 py-3 bg-primary text-white font-medium rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              <span>{currentRound === 3 ? 'Generate Report' : 'Next Question'}</span>
              <span>→</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
